import { useEffect, useState } from "react";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { doc, getFirestore, onSnapshot, updateDoc } from "firebase/firestore";
import firebase_app from "@/firebase/config";
import { ActionIcon, Box, Button, Divider, Flex, Image, Menu, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconDotsVertical, IconRotate } from "@tabler/icons-react";

const toTitleCase = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Updated interface to reflect the actual outfit data structure
interface OutfitImage {
  outfitName: string;
  outputPath: string;
  outfitType?: string;
}

interface ImageGalleryProps {
  paths: OutfitImage[];
  analysisId?: string;
  columns?: { base: number; sm: number; md?: number; lg?: number };
}

export default function ImageGallery({ paths, analysisId, columns = { base: 2, sm: 2, md: 4, lg: 4 } }: ImageGalleryProps) {
  const [imagesByType, setImagesByType] = useState<{
    [key: string]: { path: OutfitImage; url: string | null }[];
  }>({});
  const [outfitTypes, setOutfitTypes] = useState<string[]>([]);
  const [imageRotations, setImageRotations] = useState<Record<string, number>>({});
  const [isUpdatingRotations, setIsUpdatingRotations] = useState(false);
  const storage = getStorage(firebase_app);

  // The order we want to display outfit types
  const typeOrder = ["CASUAL", "BUSINESS", "FORMAL"];

  // Listen for rotation changes from Firestore
  useEffect(() => {
    if (!analysisId) return;

    const firestore = getFirestore(firebase_app);
    const docRef = doc(firestore, "generatedColorAnalyses", analysisId);

    const unsub = onSnapshot(docRef, (snap) => {
      if (snap.exists() && !isUpdatingRotations) {
        const data = snap.data();
        if (data.outfitImageRotations) {
          // Convert sanitized keys back to original paths
          const rotations: Record<string, number> = {};
          Object.entries(data.outfitImageRotations).forEach(([safeKey, value]) => {
            // Find the original path that corresponds to this sanitized key
            const originalPath = paths.find(
              (p) => p.outputPath.replace(/[~*/\[\].]/g, "_") === safeKey
            )?.outputPath;
            if (originalPath) {
              rotations[originalPath] = value as number;
            }
          });
          setImageRotations(rotations);
        }
      }
    });

    return () => unsub();
  }, [analysisId, paths, isUpdatingRotations]);

  useEffect(() => {
    const loadImages = async () => {
      // Group images by their outfitType
      const imageGroups: { [key: string]: { path: OutfitImage; url: string | null }[] } = {};

      // Process each outfit
      for (const path of paths) {
        // Default to "OTHER" if outfitType is missing
        const outfitType = path.outfitType || "OTHER";

        if (!imageGroups[outfitType]) {
          imageGroups[outfitType] = [];
        }

        try {
          const imageRef = ref(storage, path.outputPath);
          const url = await getDownloadURL(imageRef);
          imageGroups[outfitType].push({ path, url });
        } catch (error) {
          console.error("Error loading image:", error, path);
          imageGroups[outfitType].push({ path, url: null });
        }
      }

      setImagesByType(imageGroups);

      // Determine display order of types based on typeOrder
      const availableTypes = Object.keys(imageGroups);
      const orderedTypes = typeOrder
        .filter((type) => availableTypes.includes(type))
        .concat(availableTypes.filter((type) => !typeOrder.includes(type)));

      setOutfitTypes(orderedTypes);
    };

    loadImages();
  }, [paths]);

  const handleRotateImage = async (outputPath: string) => {
    const currentRotation = imageRotations[outputPath] || 0;
    const newRotation = (currentRotation + 90) % 360;

    // Update local state immediately for responsive UI
    setImageRotations((prev) => ({
      ...prev,
      [outputPath]: newRotation,
    }));

    // Save to Firestore if analysisId is provided
    if (analysisId) {
      try {
        setIsUpdatingRotations(true);
        const firestore = getFirestore(firebase_app);
        const docRef = doc(firestore, "generatedColorAnalyses", analysisId);

        // Sanitize path for Firestore field
        const safeKey = outputPath.replace(/[~*/\[\].]/g, "_");

        await updateDoc(docRef, {
          [`outfitImageRotations.${safeKey}`]: newRotation,
        });

        setTimeout(() => setIsUpdatingRotations(false), 500);
      } catch (err) {
        console.error("Failed to save image rotation:", err);
        setIsUpdatingRotations(false);
      }
    }
  };

  const handleRotateAll = async () => {
    // Update all rotations locally
    const newRotations: Record<string, number> = {};
    paths.forEach((p) => {
      const currentRotation = imageRotations[p.outputPath] || 0;
      newRotations[p.outputPath] = (currentRotation + 90) % 360;
    });

    setImageRotations(newRotations);

    // Save to Firestore if analysisId is provided
    if (analysisId) {
      try {
        setIsUpdatingRotations(true);
        const firestore = getFirestore(firebase_app);
        const docRef = doc(firestore, "generatedColorAnalyses", analysisId);

        // Convert to sanitized keys for Firestore
        const firestoreRotations: Record<string, number> = {};
        Object.entries(newRotations).forEach(([path, rotation]) => {
          const safeKey = path.replace(/[~*/\[\].]/g, "_");
          firestoreRotations[safeKey] = rotation;
        });

        await updateDoc(docRef, {
          outfitImageRotations: firestoreRotations,
        });

        setTimeout(() => setIsUpdatingRotations(false), 500);
      } catch (err) {
        console.error("Failed to save rotations:", err);
        setIsUpdatingRotations(false);
      }
    }
  };

  return (
    <>
      {paths.length > 0 && (
        <Flex justify="flex-end" mb="md">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconRotate size={14} />}
            onClick={handleRotateAll}
          >
            Rotate all
          </Button>
        </Flex>
      )}

      {outfitTypes.map((type, typeIndex) => (
        <Box key={type} mb={typeIndex < outfitTypes.length - 1 ? "xl" : 0}>
          <Divider
            label={toTitleCase(type).toUpperCase()}
            labelPosition="center"
            my="lg"
            styles={{
              label: {
                fontWeight: 600,
                fontSize: "var(--mantine-font-size-sm)",
                color: "var(--mantine-color-dimmed)",
              },
            }}
          />

          <SimpleGrid cols={columns} spacing="md">
            {imagesByType[type]
              .filter((item) => item.url !== null)
              .map((item, index) => {
                const rotation = imageRotations[item.path.outputPath] || 0;

                return (
                  <Stack key={`${type}-${index}`} gap="2px">
                    <Box
                      style={{
                        position: "relative",
                        paddingBottom: "100%",
                        height: 0,
                        overflow: "hidden",
                      }}
                    >
                      <Image
                        src={item.url!}
                        alt={item.path.outfitName || `${toTitleCase(type)} outfit ${index + 1}`}
                        radius="md"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          transform: `rotate(${rotation}deg)`,
                          transition: "transform 0.2s ease-in-out",
                        }}
                      />
                      <Menu>
                        <Menu.Target>
                          <ActionIcon
                            variant="light"
                            color="gray"
                            size="sm"
                            style={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              zIndex: 1,
                            }}
                          >
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconRotate size={16} />}
                            onClick={() => handleRotateImage(item.path.outputPath)}
                          >
                            Rotate
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Box>
                    <Text fz="xs" fw={500} ta="center" c="dimmed" top="0">
                      {item.path.outfitName}
                    </Text>
                  </Stack>
                );
              })}
          </SimpleGrid>
        </Box>
      ))}

      {outfitTypes.length === 0 && (
        <Text ta="center" c="dimmed">
          No outfits available to display.
        </Text>
      )}
    </>
  );
}
